import {
	useLocalParticipant,
	useMaybeRoomContext,
} from "@livekit/components-react";
import { captureException } from "@sentry/react";
import { Track } from "livekit-client";
import {
	ChevronUp,
	Mic,
	MicOff,
	Monitor,
	MonitorOff,
	Video,
	VideoOff,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverAnchor,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useCameraPermission } from "@/hooks/use-camera-permission";
import { useMicrophonePermission } from "@/hooks/use-microphone-permission";
import { useScreenShare } from "@/hooks/use-screen-share";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { BackgroundEffectApplier } from "./background-selector";
import { ControlBarSettings } from "./control-bar-settings";
import { ScreenSourcePicker } from "./screen-source-picker";

const BROADCAST_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
	echoCancellation: false,
	noiseSuppression: false,
	autoGainControl: false,
};

export function BroadcastSpeakerControlBar() {
	const room = useMaybeRoomContext();
	const { localParticipant } = useLocalParticipant();
	const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
	const [videoSettingsOpen, setVideoSettingsOpen] = useState(false);
	const { requestPermission: requestMicPermission } = useMicrophonePermission();
	const { requestPermission: requestCameraPermission } = useCameraPermission();
	const {
		screenShareEnabled,
		toggleScreenShare,
		handleElectronScreenShare,
		screenPickerOpen,
		setScreenPickerOpen,
		screenSources,
	} = useScreenShare({
		localParticipant,
		sentryContext: "broadcast_speaker_screen_share",
	});

	const micEnabled =
		localParticipant?.isMicrophoneEnabled &&
		localParticipant?.getTrackPublication(Track.Source.Microphone)?.track
			?.isMuted === false;

	const cameraEnabled =
		localParticipant?.isCameraEnabled &&
		localParticipant?.getTrackPublication(Track.Source.Camera)?.track
			?.isMuted === false;

	const toggleMic = async () => {
		if (!localParticipant) return;
		try {
			if (!micEnabled) {
				const granted = await requestMicPermission();
				if (!granted) return;
			}
			await localParticipant.setMicrophoneEnabled(
				!micEnabled,
				BROADCAST_AUDIO_CONSTRAINTS
			);
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, {
					tags: { context: "broadcast_speaker_mic" },
				});
			toast.error("Failed to toggle microphone");
		}
	};

	const toggleCamera = async () => {
		if (!localParticipant) return;
		try {
			if (!cameraEnabled) {
				const granted = await requestCameraPermission();
				if (!granted) return;
			}
			await localParticipant.setCameraEnabled(!cameraEnabled);
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, {
					tags: { context: "broadcast_speaker_camera" },
				});
			toast.error("Failed to toggle camera");
		}
	};

	if (!room) return null;

	return (
		<>
			<ScreenSourcePicker
				onOpenChange={setScreenPickerOpen}
				onSelect={handleElectronScreenShare}
				open={screenPickerOpen}
				sources={screenSources}
			/>
			<BackgroundEffectApplier />
			<div className="flex items-center justify-center gap-2 border-t bg-card/50 p-4 backdrop-blur-sm">
				<Popover onOpenChange={setAudioSettingsOpen} open={audioSettingsOpen}>
					<PopoverAnchor asChild>
						<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
							<Button
								className="h-12 rounded-none border-0 bg-accent px-4 transition-all hover:bg-accent-foreground/10"
								onClick={toggleMic}
								variant="ghost"
							>
								{micEnabled ? (
									<Mic className="size-5 text-foreground" />
								) : (
									<MicOff className="size-5 text-foreground" />
								)}
								<span className="sr-only">
									{micEnabled ? "Mute microphone" : "Unmute microphone"}
								</span>
							</Button>
							<div className="h-6 w-px bg-border" />
							<PopoverTrigger asChild>
								<Button
									className="h-12 rounded-none border-0 bg-accent px-2 transition-all hover:bg-accent-foreground/10"
									variant="ghost"
								>
									<ChevronUp
										className={`size-4 text-foreground transition-transform duration-200 ${
											audioSettingsOpen ? "rotate-180" : ""
										}`}
									/>
									<span className="sr-only">Audio settings</span>
								</Button>
							</PopoverTrigger>
						</div>
					</PopoverAnchor>
					<ControlBarSettings
						onOpenChange={setAudioSettingsOpen}
						open={audioSettingsOpen}
						type="audio"
					/>
				</Popover>

				<Popover onOpenChange={setVideoSettingsOpen} open={videoSettingsOpen}>
					<PopoverAnchor asChild>
						<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
							<Button
								className="h-12 rounded-none border-0 bg-accent px-4 transition-all hover:bg-accent-foreground/10"
								onClick={toggleCamera}
								variant="ghost"
							>
								{cameraEnabled ? (
									<Video className="size-5 text-foreground" />
								) : (
									<VideoOff className="size-5 text-foreground" />
								)}
								<span className="sr-only">
									{cameraEnabled ? "Turn off camera" : "Turn on camera"}
								</span>
							</Button>
							<div className="h-6 w-px bg-border" />
							<PopoverTrigger asChild>
								<Button
									className="h-12 rounded-none border-0 bg-accent px-2 transition-all hover:bg-accent-foreground/10"
									variant="ghost"
								>
									<ChevronUp
										className={`size-4 text-foreground transition-transform duration-200 ${
											videoSettingsOpen ? "rotate-180" : ""
										}`}
									/>
									<span className="sr-only">Video settings</span>
								</Button>
							</PopoverTrigger>
						</div>
					</PopoverAnchor>
					<ControlBarSettings
						onOpenChange={setVideoSettingsOpen}
						open={videoSettingsOpen}
						type="video"
					/>
				</Popover>

				<Button
					className="h-12 rounded-full border border-border bg-accent px-4 shadow-sm transition-all hover:shadow-md hover:brightness-90"
					onClick={toggleScreenShare}
					variant="ghost"
				>
					{screenShareEnabled ? (
						<MonitorOff className="size-5 text-foreground" />
					) : (
						<Monitor className="size-5 text-foreground" />
					)}
					<span className="sr-only">
						{screenShareEnabled ? "Stop sharing" : "Share screen"}
					</span>
				</Button>
			</div>
		</>
	);
}
